import {
  Get,
  List,
  ListResult,
} from '@/composables/GraphQL';

import { SpmTableFilter, SpmTableSort } from '@/types';

import {
  OperatorType,
  ShopsCustomers,
  ShopsCustomersAddresses,
  ShopsCustomersGroups,
  ShopsCustomersGroupsList,
  ShopsCustomersShopsLists,
  ShopsLists,
  EmailsRejectsShops,
  ShopsCustomersPhones,
  ShopsOrders,
  ShopsCustomersTags,
  ShopsOrdersStatus,
} from '@/types/generated-types/graphql';

import {
  Cart,
  CartProduct,
  ContactAddress,
  ContactCustomDefinition,
  ContactFile,
  ContactGroup,
  ContactReject,
  DataListResult,
  Order,
  OrderProduct,
} from '@/types/data-explorer-types';
import { SpmFilterOperator } from '@/components/table-rest-api/filters';
import LIST_ROWS from '@/components/data-explorer/utils/constants';
import { NestApiType, nestCall } from '@/composables/nestApi';
import moment from 'moment-timezone';

const isContactAnonymous = (email: string) => {
  if (email.length !== 32) {
    return false;
  }

  const hexRegex = /^[a-f0-9]{32}$/i;
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  return hexRegex.test(email) && !emailRegex.test(email);
};

export const getCustomer = async (idShopCustomer: number): Promise<ContactFile | null> => {
  const { item, err } = await Get<ShopsCustomers>({
    name: 'ShopsCustomers',
    id: idShopCustomer,
    keyName: 'id_shop_customer',
    fields: [
      'id_shop_customer',
      'id_customer_shop',
      'first_name',
      'last_name',
      'email',
      'gender',
      'birthday',
      'opt_in',
      'newsletter',
      'country',
      'lang',
      'active',
      'type',
      'deleted',
      'date_creation',
      'date_modification',
      'unsub_type',
      'date',
    ],
  });

  if (!err && item) {
    const contactFile: ContactFile = {
      isAnonymous: isContactAnonymous(item.email),
      generalInformation: {
        data: {
          idShopContact: 0,
          active: item.active,
          birthday: item.birthday || '',
          country: item.country || '',
          date: item.date,
          dateCreation: item.date_creation,
          dateModification: item.date_modification,
          email: item.email,
          firstName: item.first_name || '',
          gender: item.gender,
          idCustomerShop: item.id_customer_shop || '',
          idShopCustomer: item.id_shop_customer,
          lang: item.lang || '',
          lastName: item.last_name || '',
          newsletter: item.newsletter || '',
          type: item.type,
          unSubType: item.unsub_type,
          mobile: '',
          tags: [],
        },
        dataLoaded: true,
      },
      addresses: {
        data: [],
        dataLoaded: false,
      },
      groups: {
        data: [],
        dataLoaded: false,
      },
      rejects: {
        data: [],
        dataLoaded: false,
      },
      shops: {
        data: [],
        dataLoaded: false,
      },
      orders: {
        data: [],
        dataLoaded: false,
        total: 0,
        currentPage: 0,
      },
      carts: {
        data: [],
        dataLoaded: false,
        total: 0,
        currentPage: 0,
      },
      customData: {
        data: [],
        dataLoaded: false,
      },
    };
    if (!contactFile.isAnonymous) {
      const customersPhones = await List<ShopsCustomersPhones>(
        {
          name: 'ShopsCustomersPhones',
          settings: {
            limit: 1,
            offset: 0,
            order: [
              { field: 'id_shop_customer_address', type: 'DESC' },
            ],
            filter: [
              { field: 'id_shop_customer', operator: OperatorType.Equals, value: idShopCustomer },
              { field: 'mobile_count', operator: OperatorType.GreaterThan, value: 0 },
            ],
          },
          fields: [
            'mobile1',
            'mobile2',
          ],
        },
      );
      if (!customersPhones.err && customersPhones.items.length) {
        contactFile.generalInformation.data.mobile = customersPhones.items[0].mobile1 || customersPhones.items[0].mobile2 || '';
      }

      const customersTags = await List<ShopsCustomersTags>({
        name: 'ShopsCustomersTags',
        settings: {
          filter: [
            { field: 'id_shop_customer', operator: OperatorType.Equals, value: idShopCustomer },
            // Honor the soft-delete pattern of shops_customers_tags: only
            // show tags currently assigned to the contact.
            { field: 'deleted', value: 0, operator: OperatorType.Equals },
          ],
          limit: 0,
          offset: 0,
          order: [
            { field: 'id_shop', type: 'ASC' },
          ],
        },
        fields: ['id_shop', 'id_shop_customer', 'shopsTags{tag}'],
      });

      if (!customersTags.err && customersTags.items.length) {
        const tagSet = new Set<string>();
        customersTags.items.forEach((customerTag) => {
          customerTag.shopsTags?.forEach((tag) => {
            if (tag?.tag) {
              tagSet.add(tag.tag);
            }
          });
        });
        contactFile.generalInformation.data.tags = Array.from(tagSet).sort((a, b) => a.localeCompare(b));
      }
    }

    return contactFile;
  }

  return null;
};

export const getContact = async (
  idShopContact?: number,
): Promise<ContactFile | null> => {
  const fields = [
    'id_shop_contact',
    'id_shop_customer',
    'id_customer_shop',
    'contact_first_name',
    'contact_last_name',
    'contact_data_email',
    'contact_gender',
    'contact_birthday',
    'contact_data_sms',
    'contact_data_push',
    'contact_country',
    'contact_lang',
    'active',
    'created_at',
    'updated_at',
    'optin_data_email',
    'optin_data_sms',
    'unsub_data_email',
    'unsub_data_sms',
    'unsub_data_push',
  ];

  const params: Record<string, any> = {
    id_shop_contact: String(idShopContact),
    fields,
  };

  const result = await nestCall(
    'get',
    NestApiType.CLIENT_API,
    '/contacts',
    {},
    '',
    undefined,
    true,
    params,
  );

  if (result && result.statusCode === 200 && result.data) {
    const apiData = result.data;

    const contactFile: ContactFile = {
      isAnonymous: isContactAnonymous(apiData.contact_data?.email || ''),
      generalInformation: {
        data: {
          idShopContact: Number(apiData.id_shop_contact),
          idShopCustomer: Number(apiData.id_shop_customer),
          idCustomerShop: apiData.id_customer_shop || '',
          firstName: apiData.contact_first_name || '',
          lastName: apiData.contact_last_name || '',
          email: apiData.contact_data?.email || '',
          gender: apiData.contact_gender?.toString() || '0',
          birthday: apiData.contact_birthday || '',
          newsletter: apiData.optin_data?.email?.toString() || '0',
          country: apiData.contact_country || '',
          lang: apiData.contact_lang || '',
          active: apiData.active ? '1' : '0',
          type: 1,
          dateCreation: apiData.created_at || '',
          dateModification: apiData.updated_at || '',
          date: apiData.created_at || null,
          unSubType: apiData.unsub_data?.email || 0,
          mobile: apiData.contact_data?.sms || '',
          hasPushToken: !!apiData.contact_data?.push,
          tags: [],
          optinData: {
            email: apiData.optin_data?.email || 0,
            sms: apiData.optin_data?.sms || 0,
            push: apiData.optin_data?.push || 0,
          },
          unsubData: {
            email: apiData.unsub_data?.email || 0,
            sms: apiData.unsub_data?.sms || 0,
            push: apiData.unsub_data?.push || 0,
          },
        },
        dataLoaded: true,
      },
      addresses: {
        data: [],
        dataLoaded: false,
      },
      groups: {
        data: [],
        dataLoaded: false,
      },
      rejects: {
        data: [],
        dataLoaded: false,
      },
      shops: {
        data: [],
        dataLoaded: false,
      },
      orders: {
        data: [],
        dataLoaded: false,
        total: 0,
        currentPage: 0,
      },
      carts: {
        data: [],
        dataLoaded: false,
        total: 0,
        currentPage: 0,
      },
      customData: {
        data: [],
        dataLoaded: false,
      },
    };

    // Tags via dedicated REST endpoint that filters by id_shop_contact and
    // honors the soft-delete on shops_customers_tags.deleted.
    const idShopContactNumber = Number(apiData.id_shop_contact ?? 0);
    if (idShopContactNumber > 0) {
      try {
        const tagsResult = await nestCall(
          'get',
          NestApiType.CLIENT_API,
          `/contacts/${idShopContactNumber}/tags`,
          {},
          '',
          undefined,
          true,
        );
        if (tagsResult?.statusCode === 200 && Array.isArray(tagsResult.data)) {
          contactFile.generalInformation.data.tags = tagsResult.data
            .map((t: { tag?: string }) => t.tag ?? '')
            .filter((tag: string) => tag !== '');
        }
      } catch {
        // Silently ignore — tags section will display the empty state.
      }
    }

    return contactFile;
  }

  return null;
};

const mapAddress = (items: ShopsCustomersAddresses[]): ContactAddress[] => items.map((item) => ({
  active: item.active || '',
  address1: item.address1 || '',
  address2: item.address2 || '',
  city: item.city || '',
  country: item.country || '',
  idShopCustomerAddress: item.id_shop_customer_address,
  latitude: item.latitude || null,
  longitude: item.longitude || null,
  phone1: item.phone1 || '',
  phone2: item.phone2 || '',
  postcode: item.postcode || '',
  region: item.region || '',
  idAddressShop: item.id_address_shop || -1,
  idShopCustomer: item.id_shop_customer,
}));

const getAddresses = async (filter: SpmTableFilter[], order: SpmTableSort[] = [], limit = 0): Promise<ListResult<ShopsCustomersAddresses>> => {
  const customersAddresses = await List<ShopsCustomersAddresses>(
    {
      name: 'ShopsCustomersAddresses',
      settings: {
        limit,
        offset: 0,
        order,
        filter,
      },
      fields: [
        'id_shop_customer_address',
        'address1',
        'address2',
        'phone1',
        'phone2',
        'postcode',
        'region',
        'city',
        'country',
        'latitude',
        'longitude',
        'active',
        'id_address_shop',
        'id_shop_customer',
      ],
    },
  );
  return customersAddresses;
};

export const getContactAddresses = async (idShopCustomer: number): Promise<DataListResult> => {
  const customersAddresses = await getAddresses(
    [
      { field: 'id_shop_customer', operator: OperatorType.Equals, value: idShopCustomer },
      { field: 'active', operator: OperatorType.Equals, value: '1' },
    ],
  );
  if (!customersAddresses.err && customersAddresses.items.length) {
    return {
      total: 0,
      lists: mapAddress(customersAddresses.items),
    };
  }
  return {
    total: 0,
    lists: [],
  };
};

export const getContactAddressesNest = async (idShopCustomer: number): Promise<DataListResult> => {
  const filters = [
    { field: 'is_active', operator: SpmFilterOperator.EQUALS, values: '1' },
  ];

  const fields = [
    'address_id',
    'customer_id',
    'address_line1',
    'address_line2',
    'city',
    'postal_code',
    'region',
    'country',
    'phone_primary',
    'phone_secondary',
    'latitude',
    'longitude',
    'is_active',
    'shop_address_id',
  ];

  const params: Record<string, any> = {
    filters,
    fields,
  };

  const result = await nestCall(
    'get',
    NestApiType.CLIENT_API,
    `/customers/${idShopCustomer}/addresses`,
    {},
    '',
    undefined,
    true,
    params,
  );

  if (result && result.statusCode === 200 && result.data) {
    const addresses: ContactAddress[] = result.data.map((item: any) => ({
      idShopCustomerAddress: item.address_id,
      idShopCustomer: item.customer_id,
      address1: item.address_line1 || '',
      address2: item.address_line2 || '',
      city: item.city || '',
      postcode: item.postal_code || '',
      region: item.region || '',
      country: item.country || '',
      phone1: item.phone_primary || '',
      phone2: item.phone_secondary || '',
      latitude: item.latitude || null,
      longitude: item.longitude || null,
      active: item.is_active ? '1' : '0',
      idAddressShop: item.shop_address_id || -1,
    }));

    return {
      total: addresses.length,
      lists: addresses,
    };
  }

  return {
    total: 0,
    lists: [],
  };
};

export const getAddressByIdShopAddress = async (idAddressShop: number): Promise<ContactAddress|null> => {
  const customersAddress = await getAddresses([{ field: 'id_address_shop', operator: OperatorType.Equals, value: idAddressShop }]);
  if (!customersAddress.err && customersAddress.items.length) {
    const addresses = mapAddress(customersAddress.items);
    return addresses[0];
  }
  return null;
};

export const getContactShops = async (idShopCustomer: number, locale: string): Promise<DataListResult> => {
  const shopCustomersLists = await List<ShopsCustomersShopsLists>(
    {
      name: 'ShopsCustomersShopsLists',
      settings: {
        limit: 1,
        offset: 0,
        order: [],
        filter: [
          { field: 'sc.id_shop_customer', operator: OperatorType.Equals, value: idShopCustomer },
          { field: 'locale', operator: OperatorType.Equals, value: locale },
        ],
      },
      fields: [
        'id_shop_customer',
        'id_shop_lists',
      ],
    },
  );

  if (!shopCustomersLists.err && shopCustomersLists.items.length) {
    const idShopLists = shopCustomersLists.items[0]?.id_shop_lists;
    if (idShopLists?.length) {
      const { items, err } = await List<ShopsLists>({
        name: 'ShopsLists',
        settings: {
          filter: [
            {
              field: 'active',
              operator: OperatorType.Equals,
              value: 1,
            },
            {
              field: 'id_shop_list',
              operator: OperatorType.In,
              value: idShopLists ?? '',
            },
            {
              field: 'valid',
              operator: OperatorType.Equals,
              value: 1,
            },
          ],
          order: [
            { field: 'id_shop_list', type: 'ASC' },
          ],
          limit: 0,
          offset: 0,
        },
        fields: ['id_shop_list', 'name'],
      });

      if (items && items.length) {
        return {
          total: 0,
          lists: items.map((item) => (item.name)),
        };
      }
    }
  }
  return {
    total: 0,
    lists: [],
  };
};

export const getContactListNest = async (idContact: number): Promise<DataListResult> => {
  const result = await nestCall(
    'get',
    NestApiType.CLIENT_API,
    `/contacts/${idContact}/lists`,
    {},
    '',
    undefined,
    true,
  );

  if (result && result.statusCode === 200 && result.data) {
    return {
      total: result.data.length,
      lists: result.data,
    };
  }

  return {
    total: 0,
    lists: [],
  };
};

export const getContactCustomDefinitionsNest = async (idShopContact: number): Promise<DataListResult> => {
  const result = await nestCall(
    'get',
    NestApiType.CLIENT_API,
    `/contacts/${idShopContact}/custom-data-definitions`,
    {},
    '',
    undefined,
    true,
  );

  if (!result || result.statusCode !== 200 || !Array.isArray(result.data)) {
    return {
      total: 0,
      lists: [],
    };
  }

  const contactDefinitions: ContactCustomDefinition[] = result.data.map((item: any) => ({
    idDefinition: Number(item.id_definition),
    schemaName: item.name || '',
    description: item.description || '',
    sourceField: item.source_field || '',
  }));

  return {
    total: contactDefinitions.length,
    lists: contactDefinitions,
  };
};

export const getContactGroups = async (idShopCustomer: number): Promise<DataListResult> => {
  const resShopsCustomersGroups = await List<ShopsCustomersGroups>(
    {
      name: 'ShopsCustomersGroups',
      settings: {
        limit: 0,
        offset: 0,
        order: [
          { field: 'id_shop_customer_group', type: 'ASC' },
        ],
        filter: [{ field: 'id_shop_customer', operator: OperatorType.Equals, value: idShopCustomer }],
      },
      fields: [
        'id_shop_customer_group',
        'id_shop_customer',
        'id_shop',
        'group',
        'date_modification',
        'date',
      ],
    },
  );
  if (!resShopsCustomersGroups.err && resShopsCustomersGroups.items.length) {
    const groups = await List<ShopsCustomersGroupsList>(
      {
        name: 'ShopsCustomersGroupsList',
        settings: {
          limit: 0,
          offset: 0,
          order: [],
          filter: [
            { field: 'id_group', operator: OperatorType.In, value: resShopsCustomersGroups.items.map((item) => item.group).join(',') },
          ],
        },
        fields: [
          'id_group',
          'name',
          'id_shop',
        ],
      },
    );
    if (!groups.err && groups.items) {
      const contactGroups: ContactGroup[] = groups.items.map((item) => ({
        idGroup: item.id_group,
        name: item.name || '',
      }));
      return {
        total: 0,
        lists: contactGroups,
      };
    }
  }
  return {
    total: 0,
    lists: [],
  };
};

export const getContactGroupsNest = async (idShopCustomer: number): Promise<DataListResult> => {
  const fields = [
    'id',
    'customer_id',
    'shop_id',
    'group_id',
    'name',
    'updated_at',
    'created_at',
  ];

  const params: Record<string, any> = {
    fields,
  };

  const result = await nestCall(
    'get',
    NestApiType.CLIENT_API,
    `/customers/${idShopCustomer}/groups`,
    {},
    '',
    undefined,
    true,
    params,
  );

  if (result && result.statusCode === 200 && result.data) {
    const contactGroups: ContactGroup[] = result.data.map((item: any) => ({
      idGroup: item.id,
      name: item.name || '',
    }));

    return {
      total: contactGroups.length,
      lists: contactGroups,
    };
  }

  return {
    total: 0,
    lists: [],
  };
};

export const getContactRejects = async (idShopCustomer: number): Promise<DataListResult> => {
  const emailsRejects = await List<EmailsRejectsShops>(
    {
      name: 'EmailsRejectsShops',
      settings: {
        limit: 3,
        offset: 0,
        order: [
          { field: 'date', type: 'DESC' },
        ],
        filter: [{ field: 'id_shop_customer', operator: OperatorType.Equals, value: idShopCustomer }],
      },
      fields: [
        'id_email_reject',
        'id_shop_customer',
        'id_shop',
        'reject_type',
        'reject_from',
        'reject_message',
        'ems_category',
        'email',
        'date',
      ],
    },
  );
  if (!emailsRejects.err && emailsRejects.items.length) {
    const rejects: ContactReject[] = emailsRejects.items.map((item) => ({
      date: item.date,
      idEmailReject: item.id_email_reject,
      rejectFrom: item.reject_from || '',
      rejectMessage: item.reject_message || '',
      rejectType: item.reject_type,
    }));
    return {
      total: 0,
      lists: rejects,
    };
  }
  return {
    total: 0,
    lists: [],
  };
};

const mapOrders = (items: ShopsOrders[]): Order[] => items.map((item) => ({
  idShopOrder: item.id_shop_order,
  idOrder: item.id_order,
  orderReference: item.order_reference ?? '',
  amount: item.amount,
  dateCreation: item.date_creation,
  dateModification: item.date_modification,
  idAddressDelivery: item.id_address_delivery,
  idAddressInvoice: item.id_address_invoice,
  date: item.date,
  currency: item.currency ?? 'EUR',
  status: item.status ?? '',
  amountHT: item.amount_without_tax,
  voucherAmount: item.voucher_amount || null,
  firstname: item.first_name || '',
  lastname: item.last_name || '',
  idCustomerShop: item.id_customer_shop || '',
  email: item.email || '',
  idShopCustomer: item.id_shop_customer,
  dateCart: item.date_cart,
  idCart: item.id_cart,
  isValid: item.is_valid,
  shipping: item.shipping,
  shippingHT: item.shipping_without_tax,
  carrierName: item.name ?? '',
  shippingNumber: item.shipping_number ?? '',
  voucher: item.voucher ?? '',
  isAnonymous: isContactAnonymous(item.email || ''),
  deliveryAddress: {
    data: [],
    dataLoaded: false,
  },
  invoiceAddress: {
    data: [],
    dataLoaded: false,
  },
  products: {
    data: [],
    dataLoaded: false,
  },
  statusLoaded: false,
}));

const getOrders = async (filter: SpmTableFilter[], limit = 0, offset = 0): Promise<ListResult<ShopsOrders>> => {
  const orders = await List<ShopsOrders>(
    {
      name: 'ShopsOrdersCustom',
      settings: {
        limit,
        offset,
        order: [
          { field: 'so.date', type: 'DESC' },
        ],
        filter,
      },
      fields: [
        'id_shop_order',
        'id_order',
        'order_reference',
        'amount',
        'date_creation',
        'date_modification',
        'id_address_delivery',
        'id_address_invoice',
        'date',
        'currency',
        'voucher_amount',
        'amount_without_tax',
        'first_name',
        'last_name',
        'id_customer_shop',
        'email',
        'id_shop_customer',
        'is_valid',
        'shipping_number',
        'voucher',
        'shipping',
        'shipping_without_tax',
        'id_cart',
        'date_cart',
        'name',
        'voucher',
        'status',
      ],
    },
  );
  return orders;
};

export const getContactOrdersNest = async (idShopContact: number, offset = 0, limit = LIST_ROWS): Promise<DataListResult> => {
  const filters = [
    { field: 'contact_id', operator: SpmFilterOperator.EQUALS, values: idShopContact },
  ];

  const fields = [
    'order_id',
    'customer_id',
    'id',
    'reference',
    'amount',
    'amount_without_tax',
    'currency',
    'date',
    'created_at',
    'updated_at',
    'address_delivery_id',
    'address_invoice_id',
    'cart_id',
    'cart_updated_at',
    'is_confirmed',
    'voucher_value',
    'voucher_used',
    'shipping_cost',
    'shipping_cost_tax_excluded',
    'shipping_number',
    'carrier_name',
    'status_name',
    'customer_first_name',
    'customer_last_name',
    'customer_email',
    'contact_id',
    'data_source_id',
  ];

  const params: Record<string, any> = {
    filters,
    fields,
    offset,
    limit,
  };

  const result = await nestCall(
    'get',
    NestApiType.CLIENT_API,
    '/orders',
    {},
    '',
    undefined,
    true,
    params,
  );

  if (result && result.statusCode === 200 && result.data) {
    const normalizeDate = (d: any): string => d ? moment(d).format('YYYY-MM-DD HH:mm:ss') : '';
    const orders: Order[] = result.data.map((item: any) => ({
      // API `id` = id_shop_order (interne ShopiMind), `order_id` = id_order (marchand).
      idShopOrder: item.id,
      idOrder: item.order_id,
      orderReference: item.reference,
      amount: item.amount,
      amountHT: item.amount_without_tax,
      currency: item.currency,
      date: normalizeDate(item.date),
      dateCreation: item.created_at,
      dateModification: item.updated_at,
      idAddressDelivery: item.address_delivery_id,
      idAddressInvoice: item.address_invoice_id,
      idCart: item.cart_id,
      dateCart: item.cart_updated_at,
      isValid: item.is_confirmed,
      voucherAmount: item.voucher_value,
      voucher: item.voucher_used,
      shipping: item.shipping_cost,
      shippingHT: item.shipping_cost_tax_excluded,
      shippingNumber: item.shipping_number,
      carrierName: item.carrier_name,
      status: item.status_name,
      firstname: item.customer_first_name,
      lastname: item.customer_last_name,
      email: item.customer_email,
      idCustomerShop: String(item.customer_id),
      idShopCustomer: item.customer_id,
      deliveryAddress: {
        data: [],
        dataLoaded: false,
      },
      invoiceAddress: {
        data: [],
        dataLoaded: false,
      },
      statusLoaded: false,
      idShopContact: item.contact_id || null,
      dataSourceId: item.data_source_id || null,
      products: {
        data: [],
        dataLoaded: false,
      },
    }));

    return {
      total: result.meta?.pagination?.total_items || orders.length,
      lists: orders,
    };
  }

  return {
    total: 0,
    lists: [],
  };
};

export const getAddressOrders = async (idAddress: number, field: string, offset = 0): Promise<DataListResult> => {
  const shopOrders = await getOrders([{ field, operator: OperatorType.Equals, value: idAddress }], LIST_ROWS, offset);
  if (!shopOrders.err && shopOrders.items.length) {
    return {
      total: shopOrders.total,
      lists: mapOrders(shopOrders.items),
    };
  }
  return {
    total: 0,
    lists: [],
  };
};

export const getContactOrders = async (idShopCustomer: number, offset = 0): Promise<DataListResult> => {
  const shopOrders = await getOrders([{ field: 'so.id_shop_customer', operator: OperatorType.Equals, value: idShopCustomer }], LIST_ROWS, offset);
  if (!shopOrders.err && shopOrders.items.length) {
    return {
      total: shopOrders.total,
      lists: mapOrders(shopOrders.items),
    };
  }
  return {
    total: 0,
    lists: [],
  };
};

export const getOrderNest = async (idOrder: number): Promise<Order|null> => {
  const filters = [
    { field: 'id', operator: SpmFilterOperator.EQUALS, values: idOrder },
  ];

  const fields = [
    'order_id',
    'customer_id',
    'id',
    'reference',
    'amount',
    'amount_without_tax',
    'currency',
    'date',
    'created_at',
    'updated_at',
    'address_delivery_id',
    'address_invoice_id',
    'cart_id',
    'cart_updated_at',
    'is_confirmed',
    'voucher_value',
    'voucher_used',
    'shipping_cost',
    'shipping_cost_tax_excluded',
    'shipping_number',
    'carrier_name',
    'status_name',
    'customer_first_name',
    'customer_last_name',
    'customer_email',
    'contact_id',
    'data_source_id',
  ];

  const params: Record<string, any> = {
    filters,
    fields,
    offset: 0,
    limit: 1,
  };

  const result = await nestCall(
    'get',
    NestApiType.CLIENT_API,
    '/orders',
    {},
    '',
    undefined,
    true,
    params,
  );

  if (result && result.statusCode === 200 && result.data && result.data.length > 0) {
    const item = result.data[0];
    const normalizeDate = (d: any): string => d ? moment(d).format('YYYY-MM-DD HH:mm:ss') : '';
    const order: Order = {
      // API `id` = id_shop_order (interne ShopiMind), `order_id` = id_order (marchand).
      idShopOrder: item.id,
      idOrder: item.order_id,
      orderReference: item.reference,
      amount: item.amount,
      amountHT: item.amount_without_tax,
      currency: item.currency,
      date: normalizeDate(item.date),
      dateCreation: item.created_at,
      dateModification: item.updated_at,
      idAddressDelivery: item.address_delivery_id,
      idAddressInvoice: item.address_invoice_id,
      idCart: item.cart_id,
      dateCart: item.cart_updated_at,
      isValid: item.is_confirmed,
      voucherAmount: item.voucher_value,
      voucher: item.voucher_used,
      shipping: item.shipping_cost,
      shippingHT: item.shipping_cost_tax_excluded,
      shippingNumber: item.shipping_number,
      carrierName: item.carrier_name,
      status: item.status_name,
      firstname: item.customer_first_name,
      lastname: item.customer_last_name,
      email: item.customer_email,
      idCustomerShop: String(item.customer_id),
      idShopCustomer: item.customer_id,
      idShopContact: item.contact_id || null,
      dataSourceId: item.data_source_id || null,
      deliveryAddress: {
        data: [],
        dataLoaded: false,
      },
      invoiceAddress: {
        data: [],
        dataLoaded: false,
      },
      statusLoaded: false,
      products: {
        data: [],
        dataLoaded: false,
      },
    };

    return order;
  }

  return null;
};

export const getOrder = async (idShopOrder: number, field = 'id_shop_order'): Promise<Order|null> => {
  const shopOrders = await getOrders([{ field: `so.${field}`, operator: OperatorType.Equals, value: idShopOrder }], 1);
  if (!shopOrders.err && shopOrders.items.length) {
    const orders = mapOrders(shopOrders.items);
    return orders[0];
  }
  return null;
};

export const getOrderStatus = async (status: any, locale: string): Promise<string> => {
  const ordersStatus = await List<ShopsOrdersStatus>(
    {
      name: 'ShopsOrdersStatus',
      settings: {
        limit: 1,
        offset: 0,
        order: [],
        filter: [
          { field: 'id_status', operator: OperatorType.Equals, value: status },
          { field: 'lang', operator: OperatorType.Equals, value: locale.toLowerCase() },
        ],
      },
      fields: [
        'name',
      ],
    },
  );
  if (!ordersStatus.err && ordersStatus.items.length) {
    const orderStatus = ordersStatus.items[0];
    return orderStatus.name;
  }
  return '';
};

export const getOrdersProducts = async (idShopOrder: number, locale: string): Promise<DataListResult> => {
  const ordersProducts = await List<any>(
    {
      name: 'ShopsOrdersProducts',
      settings: {
        limit: 0,
        offset: 0,
        order: [],
        filter: [
          { field: 'sop.id_shop_order', operator: OperatorType.Equals, value: idShopOrder },
          { field: 'sp.lang', operator: OperatorType.Equals, value: locale.toLowerCase() },
        ],
      },
      fields: [
        'id_shop_order_product',
        'id_shop_product',
        'id_shop_product_attribute',
        'id_product',
        'id_combination',
        'id_manufacturer',
        'price',
        'qty',
        'name',
        'spLink',
        'spImage_link',
        'reference',
        'spaLink',
        'spaImage_link',
        'spaReference',
        'combination_name',
      ],
    },
  );

  if (!ordersProducts.err && ordersProducts.items.length) {
    const products = ordersProducts.items.map((item) => {
      const orderProduct: OrderProduct = {
        idShopOrderProduct: item.id_shop_order_product,
        idShopProduct: item.id_shop_product,
        idShopProductAttribute: item.id_shop_product_attribute,
        idProduct: item.id_product,
        idCombination: item.id_combination,
        price: item.price,
        quantity: item.qty,
        productReference: item.reference,
        productName: item.name,
        productLink: item.spLink,
        productImageLink: item.spImage_link,
        combinationName: item.combination_name,
        combinationLink: item.spaLink,
        combinationImageLink: item.spaImage_link,
        combinationReference: item.spaReference,
      };
      return orderProduct;
    });
    return {
      total: 0,
      lists: products,
    };
  }
  return {
    total: 0,
    lists: [],
  };
};

const getCarts = async (filter: SpmTableFilter[], limit = 0, offset = 0): Promise<ListResult<any>> => {
  const orders = await List<any>(
    {
      name: 'ShopsCartsCustom',
      settings: {
        limit,
        offset,
        order: [
          { field: 'sc.date', type: 'DESC' },
        ],
        filter,
      },
      fields: [
        'id_shop_cart',
        'id_shop_customer',
        'id_cart',
        'amount',
        'amount_without_tax',
        'voucher_amount',
        'currency',
        'voucher',
        'is_valid',
        'date_modification',
        'date',
        'hash',
        'first_name',
        'last_name',
        'id_customer_shop',
        'email',
      ],
    },
  );
  return orders;
};

const mapCarts = (items: any[]): Cart[] => items.map((item) => ({
  idShopCart: item.id_shop_cart,
  idShopCustomer: item.id_shop_customer,
  idCart: item.id_cart,
  amount: item.amount,
  amountHT: item.amount_without_tax,
  voucherAmount: item.voucher_amount || 0,
  currency: item.currency || 'EUR',
  voucher: item.voucher || '',
  isValid: item.is_valid,
  dateModification: item.date_modification,
  date: item.date,
  hash: item.hash,
  firstname: item.first_name || '',
  lastname: item.last_name || '',
  idCustomerShop: item.id_customer_shop,
  email: item.email,
  products: {
    data: [],
    dataLoaded: false,
  },
  isAnonymous: isContactAnonymous(item.email || ''),
}));

export const getCart = async (idCart: string): Promise<Cart|null> => {
  const shopCarts = await getCarts([{ field: 'sc.id_cart', operator: OperatorType.Equals, value: idCart }], 1);
  if (!shopCarts.err && shopCarts.items.length) {
    const carts = mapCarts(shopCarts.items);
    return carts[0];
  }
  return null;
};

export const getContactCarts = async (idShopCustomer: number, offset = 0): Promise<DataListResult> => {
  const shopCarts = await getCarts([{ field: 'scu.id_shop_customer', operator: OperatorType.Equals, value: idShopCustomer }], LIST_ROWS, offset);
  if (!shopCarts.err && shopCarts.items.length) {
    return {
      total: shopCarts.total,
      lists: mapCarts(shopCarts.items),
    };
  }
  return {
    total: 0,
    lists: [],
  };
};

export const getContactCartsNest = async (idShopContact: number, offset = 0, limit = LIST_ROWS): Promise<DataListResult> => {
  const filters = [
    { field: 'contact_id', operator: SpmFilterOperator.EQUALS, values: idShopContact },
  ];

  const fields = [
    'cart_id',
    'customer_id',
    'id',
    'amount',
    'amount_without_tax',
    'voucher_amount',
    'currency',
    'voucher',
    'is_valid',
    'updated_at',
    'date',
    'hash',
    'customer_first_name',
    'customer_last_name',
    'customer_email',
    'contact_id',
    'data_source_id',
  ];

  const params: Record<string, any> = {
    filters,
    fields,
    offset,
    limit,
  };

  const result = await nestCall(
    'get',
    NestApiType.CLIENT_API,
    '/carts',
    {},
    '',
    undefined,
    true,
    params,
  );

  if (result && result.statusCode === 200 && result.data) {
    const carts: Cart[] = result.data.map((item: any) => ({
      idShopCart: item.cart_id,
      idShopCustomer: item.customer_id,
      idCart: item.id,
      amount: item.amount,
      amountHT: item.amount_without_tax,
      voucherAmount: item.voucher_amount || 0,
      currency: item.currency || 'EUR',
      voucher: item.voucher || '',
      isValid: item.is_valid,
      dateModification: item.updated_at,
      date: item.date,
      hash: item.hash,
      firstname: item.customer_first_name || '',
      lastname: item.customer_last_name || '',
      idCustomerShop: String(item.customer_id),
      email: item.customer_email,
      idShopContact: item.contact_id || null,
      dataSourceId: item.data_source_id || null,
      products: {
        data: [],
        dataLoaded: false,
      },
      isAnonymous: isContactAnonymous(item.customer_email || ''),
    }));

    return {
      total: result.meta?.pagination?.total_items || carts.length,
      lists: carts,
    };
  }

  return {
    total: 0,
    lists: [],
  };
};

export const getCartProducts = async (idShopCart: number, locale: string): Promise<DataListResult> => {
  const cartProducts = await List<any>(
    {
      name: 'ShopsCartsProductsCustom',
      settings: {
        limit: 0,
        offset: 0,
        order: [],
        filter: [
          { field: 'scp.id_shop_cart', operator: OperatorType.Equals, value: idShopCart },
          { field: 'sp.lang', operator: OperatorType.Equals, value: locale.toLowerCase() },
        ],
      },
      fields: [
        'id_shop_cart_product',
        'id_shop_product',
        'id_shop_product_attribute',
        'id_product',
        'id_combination',
        'id_manufacturer',
        'price',
        'qty',
        'name',
        'spLink',
        'spImage_link',
        'reference',
        'spaLink',
        'spaImage_link',
        'spaReference',
        'combination_name',
      ],
    },
  );

  if (!cartProducts.err && cartProducts.items.length) {
    const products = cartProducts.items.map((item) => {
      const cartProduct: CartProduct = {
        idShopCartProduct: item.id_shop_cart_product,
        idShopProduct: item.id_shop_product,
        idShopProductAttribute: item.id_shop_product_attribute,
        idProduct: item.id_product,
        idCombination: item.id_combination,
        price: item.price,
        quantity: item.qty,
        productReference: item.reference,
        productName: item.name,
        productLink: item.spLink,
        productImageLink: item.spImage_link,
        combinationName: item.combination_name,
        combinationLink: item.spaLink,
        combinationImageLink: item.spaImage_link,
        combinationReference: item.spaReference,
      };
      return cartProduct;
    });
    return {
      total: 0,
      lists: products,
    };
  }
  return {
    total: 0,
    lists: [],
  };
};
